import {create} from 'zustand'
import {devtools} from 'zustand/middleware'

import { IFinalityActions, IFinalityState, IFinalityCardData, IFinalityCardFilesList, IFinalityCardTrackNumbersList, IFinalityFilter, IFinalityPrimaryList } from './state.types'
import { getFinalityCardData, getFinalityFilesList, getFinalityList, getFinalityTrackNumbersList, getPaymentFilesOnContract, saveFinalityCardData, saveFinalityTrackNumber, telegramApplicationDeliveryToClient, telegramFinalitySendingEndCloseContract, telegramFinalitySendingFiles, telegramPaymentInvoiceToClint } from './api'
import { createLatestRequestController } from '../latest-request'


/** Начальное состояние хранилища */
export const initialStateFinality: IFinalityState = {
    errors: null,

    isReloadContracts: false,
    reloadTick: 0,
    isLoadingCard: false,
    isLoadingCardFiles: false,
    isLoadingCardTrackNumbers: false,

    contracts: {
        isLoading: false,

        items: [] as IFinalityPrimaryList[],
        totalCount: 0,
        pageSize: 0,
        currentPage: 0
    },
}

const finalityListRequest = createLatestRequestController()


/** Хранилище страницы - Доставка */
export const useFinality = create<IFinalityState & IFinalityActions>()(devtools((set) => ({
    ...initialStateFinality,

    /** Сброс хранилища до начального состояния */
    clearFinalityState: () => {
        finalityListRequest.invalidate()
        set(initialStateFinality)
    },

    /** Запрос на получение первичных данных, при загрузке страницы. */
    actionGetFinalityList: async (pageNumber: number, search: string, filter?: IFinalityFilter) => {
        const request = finalityListRequest.start()
        set((state) => ({ contracts: { ...state.contracts, isLoading: !state.isReloadContracts } }))

        const res = await getFinalityList(pageNumber, search, filter, request.signal)
        if (!request.isCurrent()) return
        request.finish()

        if (res.status !== 200) set({ errors: {type: res.status === 500 ? 'error' : 'warning', message: res.message} })

        if (res.message === 'OK') {
            set({errors: null})
            set({isReloadContracts: false})

            set((state) => ({
                contracts: {
                    ...state.contracts,

                    items: res.data!.items,
                    totalCount: res.data!.totalCount,
                    pageSize: res.data!.pageSize,
                    currentPage: res.data!.currentPage
                }
            }))
        }

        set((state) => ({contracts: {...state.contracts, isLoading: false}}))
    },



    /** Запрос на получение данных по анкете */
    actionGetFinalityCardData: async (contractID: number) => {
        let returnData: IFinalityCardData | null = null
        set({isLoadingCard: true})

        const res = await getFinalityCardData(contractID)
        if (res.status !== 200) set({ errors: {type: res.status === 500 ? 'error' : 'warning', message: res.message} })

        if (res.message === 'OK') {
            set({errors: null})
            if (res.data) returnData = res.data
        }

        set({isLoadingCard: false})

        return returnData
    },
    /** Запрос на получение списка файлов по анкете */
    actionGetFinalityFilesList: async (contractID: number) => {
        let returnData: IFinalityCardFilesList[] = []
        set({isLoadingCardFiles: true})

        const res = await getFinalityFilesList(contractID)
        if (res.status !== 200) set({ errors: {type: res.status === 500 ? 'error' : 'warning', message: res.message} })

        if (res.message === 'OK') {
            set({errors: null})

            if (res.data) returnData = res.data?.files
        }

        set({isLoadingCardFiles: false})
        return returnData
    },
    /** Запрос на получение списка трек-номеров по анкете */
    actionGetFinalityTrackNumbersList: async (contractID: number) => {
        let returnData: IFinalityCardTrackNumbersList[] = []
        set({isLoadingCardTrackNumbers: true})

        const res = await getFinalityTrackNumbersList(contractID)
        if (res.status !== 200) set({ errors: {type: res.status === 500 ? 'error' : 'warning', message: res.message} })

        if (res.message === 'OK') {
            set({errors: null})

            if (res.data) returnData = res.data?.trackNumbers
        }

        set({isLoadingCardTrackNumbers: false})
        return returnData
    },
    /** Запрос на Создание/Редактирование трэк-номеров по анкете */
    actionSaveFinalityTrackNumber: async (contractID: number, deliveryID: number, action: string, trackNumber: string, trackNumberComment: string) => {
        set({isLoadingCardTrackNumbers: true})

        const res = await saveFinalityTrackNumber(contractID, deliveryID, action, trackNumber, trackNumberComment)
        if (res.status !== 200) set({ errors: {type: res.status === 500 ? 'error' : 'warning', message: res.message} })

        if (res.message === 'OK') set({ errors: null })

        set({isLoadingCardTrackNumbers: false})
        return res.status
    },
    /** Запрос на сохранение данных по анкете */
    actionSaveFinalityCardData: async (contractID: number, deliveryToWhom: string, deliveryAddress: string, myComment: string, isAddressConfirmed: number, isDeliveryCDEK: number) => {
        set({isLoadingCard: true})

        const res = await saveFinalityCardData(contractID, deliveryToWhom, deliveryAddress, myComment, isAddressConfirmed, isDeliveryCDEK)
        if (res.status !== 200) set({ errors: {type: res.status === 500 ? 'error' : 'warning', message: res.message} })

        if (res.message === 'OK') {
            set({ errors: null })
            set((state) => ({ reloadTick: state.reloadTick + 1 }))
        }

        set({isLoadingCard: false})
        return res.status
    },



    /** Запрос на получения списка файлов счетов по договору */
    actionGetPaymentFilesOnContract: async (contractID: number) => {
        let returnData: {fileName: string}[] = []
        set({isLoadingCardFiles: true})

        const res = await getPaymentFilesOnContract(contractID)
        if (res.status !== 200) set({ errors: {type: res.status === 500 ? 'error' : 'warning', message: res.message} })

        if (res.message === 'OK') {
            set({errors: null})

            if (res.data) returnData = res.data?.files
        }

        set({isLoadingCardFiles: false})
        return returnData
    },



    /** Запрос на отправку информации в telegram канал (ИКТ - Сканы дипломов) */
    actionTelegramFinalitySendingFiles: async (contractListenerID: number) => {
        set({isLoadingCard: true})

        const res = await telegramFinalitySendingFiles(contractListenerID)
        if (res.status !== 200) set({ errors: {type: res.status === 500 ? 'error' : 'warning', message: res.message} })

        if (res.message === 'OK') set({ errors: null })

        set({isLoadingCard: false})
        return res.status
    },
    /** Запрос на завершение обучения и отправка информации в telegram канал (ИКТ - Доставка) */
    actionTelegramFinalitySendingEndCloseContract: async (contractID: number, byTrackNumber: string, skipStatusUpdate?: boolean) => {
        set({isLoadingCard: true})

        const res = await telegramFinalitySendingEndCloseContract(contractID, byTrackNumber, skipStatusUpdate)
        if (res.status !== 200) set({ errors: {type: res.status === 500 ? 'error' : 'warning', message: res.message} })

        if (res.message === 'OK') {
            set({ errors: null })
            set((state) => ({ reloadTick: state.reloadTick + 1 }))
        }

        set({isLoadingCard: false})
        return res.status
    },

    /** Отправка счёта в канал срочных сообщений сайта договора */
    actionTelegramPaymentInvoiceToClint: async (contractID: number, paymentInvoiceType: number, namefilesToSend: string[]) => {
        set({isLoadingCardFiles: true})

        const res = await telegramPaymentInvoiceToClint(contractID, paymentInvoiceType, namefilesToSend)
        if (res.status !== 200) set({ errors: {type: res.status === 500 ? 'error' : 'warning', message: res.message} })

        if (res.message === 'OK') set({ errors: null })

        set({isLoadingCardFiles: false})
        return res.status
    },
    /** Отправка заявления на доставку в канал срочных сообщений сайта договора */
    actionTelegramApplicationDeliveryToClient: async (contractID: number) => {
        set({isLoadingCard: true})

        const res = await telegramApplicationDeliveryToClient(contractID)
        if (res.status !== 200) set({ errors: {type: res.status === 500 ? 'error' : 'warning', message: res.message} })

        if (res.message === 'OK') {
            set({ errors: null })
            set((state) => ({ reloadTick: state.reloadTick + 1 }))
        }

        set({isLoadingCard: false})
        return res.status
    },

}), {name: 'finalityState'}))